import { Bot, ChevronDown, LogOut, Menu, PanelLeft, Settings2, User as UserIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAdminSurface } from '../lib/surface';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { useProjects } from '../lib/queries';
import { disconnectSocket, getSocket } from '../lib/socket';
import { useSidebarStore } from '../lib/sidebarStore';
import ThemeToggle from './ThemeToggle';
import { Avatar, Badge, Button, IconButton } from './ui';

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const { data: projects } = useProjects();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { toggle } = useSidebarStore();

  const isProjectRoute = Boolean(projectId || location.pathname.startsWith('/projects/'));

  // Open the shared socket once the user is known; tear it down on sign-out.
  useEffect(() => {
    if (!user) return;
    const socket = getSocket();
    const onError = (err: Error) => {
      if (import.meta.env.DEV) console.warn('[realtime]', err.message);
    };
    socket.on('connect_error', onError);
    return () => {
      socket.off('connect_error', onError);
    };
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const activeProject = projects?.find((p) => p.id === projectId);

  return (
    <div className="flex h-screen flex-col bg-ink-950">
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 sm:gap-4 border-b border-ink-850 px-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {isProjectRoute && (
            <IconButton
              label="Sidebar-ni ochish / yopish"
              variant="ghost"
              size="sm"
              onClick={toggle}
              className="text-ink-400 hover:text-ink-200 shrink-0"
            >
              <Menu className="size-4.5 sm:hidden" />
              <PanelLeft className="hidden size-4 sm:block" />
            </IconButton>
          )}

          <Link to="/" className="flex shrink-0 items-center gap-2">
            <div className="flex size-6 items-center justify-center rounded-md bg-brand-500 text-[11px] font-bold text-white">
              F
            </div>
            <span className="text-sm font-semibold tracking-tight">Forge</span>
          </Link>

          {activeProject && (
            <>
              <span className="text-ink-700">/</span>
              <div className="flex min-w-0 items-center gap-2">
                <Link
                  to={`/projects/${activeProject.id}`}
                  className="max-w-[110px] sm:max-w-xs truncate text-sm font-medium text-ink-200 transition-colors hover:text-ink-100"
                >
                  {activeProject.name}
                </Link>
                <Badge tone="neutral" className="hidden sm:inline-flex">
                  {activeProject.role}
                </Badge>
              </div>
            </>
          )}
        </div>

        <div className="relative flex items-center gap-2" ref={menuRef}>
          <ThemeToggle />
          {user?.globalRole === 'superadmin' && (
            <Badge tone="brand" className="hidden sm:inline-flex">
              superadmin
            </Badge>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-ink-850"
          >
            <Avatar name={user?.name ?? '?'} color={user?.avatarColor} size={24} />
            <span className="hidden text-xs text-ink-300 sm:inline">{user?.name}</span>
            <ChevronDown className="size-3.5 text-ink-500" />
          </button>

          {menuOpen && (
            <div className="forge-fade-in absolute top-full right-0 z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-ink-750 bg-ink-900 shadow-2xl">
              <div className="border-b border-ink-800 px-3.5 py-3">
                <p className="truncate text-sm font-medium text-ink-100">{user?.name}</p>
                <p className="truncate text-xs text-ink-400">{user?.email}</p>
              </div>
              <div className="p-1">
                {/* Settings only exists on the builder; linking to it from the
                    viewing app would bounce straight back to the project list. */}
                {activeProject && isAdminSurface && (
                  <Link
                    to={`/projects/${activeProject.id}/settings`}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs text-ink-300 transition-colors hover:bg-ink-850 hover:text-ink-100"
                  >
                    <Settings2 className="size-3.5" />
                    Project settings
                  </Link>
                )}
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs text-ink-300 transition-colors hover:bg-ink-850 hover:text-ink-100"
                >
                  <UserIcon className="size-3.5" />
                  All projects
                </Link>
                {/* Instance-wide, and above any one project: an admin token
                    reaches every project the account does. */}
                {user?.globalRole === 'superadmin' && isAdminSurface && (
                  <Link
                    to="/instance/mcp"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-xs text-ink-300 transition-colors hover:bg-ink-850 hover:text-ink-100"
                  >
                    <Bot className="size-3.5" />
                    MCP for AI clients
                  </Link>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    setMenuOpen(false);
                    disconnectSocket();
                    await logout();
                    toast.success('Signed out');
                    navigate('/login', { replace: true });
                  }}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-xs text-rose-400 transition-colors hover:bg-rose-500/10"
                >
                  <LogOut className="size-3.5" />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
